import React, { useContext, useEffect, useCallback, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import _, { set } from 'lodash'
import { runInAction } from 'mobx'
import { Props, components as reactSelectComponents } from 'react-select'

// evertel
import { useService } from '@evertel/di'
import { CustomAsyncPaginate, CustomOptionProps } from '@evertel/web/ui'
import { RepliesViewController } from '@evertel/message'
import { RoomContext } from '../RoomContext'
import { ReplyTile } from '@evertel/web/message'
import { DisplayRoomMessage } from '@evertel/stores'
import { APIDataMediaToNormalizedMedia } from '@evertel/media'
import { FetchUserService } from '@evertel/blue-user'

interface RoomRepliesSearchSelectProps extends Omit<Props, 'maxMenuHeight' | 'noOptionsMessage'> {
    selected?: DisplayRoomMessage[]
    isDisabled?: boolean
    placeholder?: string
    maxMenuHeight?: number|string
    className?: string
    onEndReachedThreshold?: number
    noOptionsMessage?: string
    onSelect?: (reply: DisplayRoomMessage) => void
}

const RoomRepliesSearchSelect: React.FC<RoomRepliesSearchSelectProps> = observer(({
    selected = [],
    isDisabled,
    placeholder = 'Search replies...',
    noOptionsMessage = 'No replies found',
    onSelect,
    maxMenuHeight,
    className,
    onEndReachedThreshold = 80,
    ...otherProps
}) => {

    const [loadingNotification, setLoadingNotification] = useState(false)

    const { roomController } = useContext(RoomContext)
    const fetchUserService = useService(FetchUserService, [])
    const repliesViewController = useService(RepliesViewController, [roomController.roomId])

    useEffect(() => {
        if (roomController.roomId) {
            repliesViewController.init(roomController.roomId)
        }
    }, [roomController.roomId])

    const retrieveRepliesUsers = useCallback(() => {
        const ownerIds = repliesViewController.replies.map(m => m.ownerId)
        const forwardedOwnerIds = repliesViewController.replies
            .filter(m => m.type === 'forwarded' && _.get(m, 'meta.forwardedMessage.ownerId'))
            .map(m => m.meta.forwardedMessage.ownerId)

        fetchUserService.fetchSpecificUsersFromRoom(roomController.roomId, [...ownerIds, ...forwardedOwnerIds])
    }, [repliesViewController, roomController.roomId, fetchUserService])

    useEffect(() => {
        if (repliesViewController.replies.length) {
            retrieveRepliesUsers()
        }
    }, [repliesViewController.replies, retrieveRepliesUsers])

    const search = useCallback(async (
        searchString: string,
        loadedOptions: DisplayRoomMessage[],
        { page }: { page: number }
    ) => {
        if (repliesViewController.searchTerm !== searchString) {
            repliesViewController.setSearchTerm(searchString)
        }

        repliesViewController.setPage(page)

        try {
            await repliesViewController.search()
        } catch (error) {
            console.error('ERROR: RoomRepliesSearchSelect search():', error)
        }

        const hasMore = (repliesViewController.repliesLatestFetch.length >= repliesViewController.limit)

        return {
            options: repliesViewController.repliesLatestFetch || [],
            hasMore,
            additional: {
                page: page + 1
            }
        }
    }, [roomController.roomId])

    const shouldLoadMore = (scrollHeight: number, clientHeight: number, scrollTop: number): boolean => {
        if (repliesViewController.isLoading) return false
        const maxPixelThreshold = 300
        const percentThreshold = 100 - onEndReachedThreshold

        const pxFromBottom = scrollHeight - clientHeight - scrollTop
        const percentThresholdFromBottomInPx = ((percentThreshold / 100) * (scrollHeight - clientHeight))

        const thresholdToUse = Math.min(percentThresholdFromBottomInPx, maxPixelThreshold)

        return pxFromBottom <= thresholdToUse
    }

    const onSelectOption = (reply: DisplayRoomMessage) => {
        if (onSelect) onSelect(reply)
    }

    const updateNotifyState = async (notifyState, message: DisplayRoomMessage) => {
        try {
            if (!message.id) return
            if (message.meta?.repliesSubscription?.notify !== undefined && notifyState === message.meta?.repliesSubscription?.notify) return

            setLoadingNotification(true)
            const newNotify = (notifyState !== undefined) ? notifyState : !message.meta?.repliesSubscription?.notify as boolean
            await roomController.updateRepliesNotification(newNotify, message.id)
            runInAction(() => {
                set(message, 'meta.repliesSubscription.notify', newNotify)
            })
        } catch (e) {
            /** nothing */
        } finally {
            setLoadingNotification(false)
        }
    }

    return (
        <CustomAsyncPaginate
            name="room-replies"
            keyName={roomController.roomId}
            searchOnly={true}
            loadOptions={search}
            debounceTimeout={300}
            additional={{
                page: 1
            }}
            defaultOptions
            shouldLoadMore={shouldLoadMore}
            loadingMessage={() => <span role="alert" aria-live="polite">Loading replies...</span>}
            value={selected}
            maxMenuHeight={maxMenuHeight}
            isDisabled={isDisabled}
            openMenuOnClick
            onChange={(message: DisplayRoomMessage, test) => {
                // this will trigger when clicking or using arrows from select options
                // instead disabled the visual indication of this and disabled
            }}
            noOptionsMessage={() => <span role="alert" aria-live="polite">{noOptionsMessage}</span>}
            placeholder={placeholder}
            className={classNames('no-bg space-below reply-tile-select', className)}
            components={{ Input: () => null, Option: ReplyOption }}
            customProps={{
                onSelect: onSelectOption,
                updateNotifyState: updateNotifyState,
                loadingNotification,
                room: roomController.room
            }}
            {...otherProps}
        />
    )
})

interface SelectCustomProps {
    data: DisplayRoomMessage
    onSelect: (reply: DisplayRoomMessage) => void
    updateNotifyState: (notifyState: boolean | null, message: DisplayRoomMessage) => void
    loadingNotification: boolean
    room: any
}

const ReplyOption = observer((props: CustomOptionProps<DisplayRoomMessage, SelectCustomProps>) => {
    const { data, selectProps } = props
    const { onSelect, updateNotifyState, loadingNotification, room } = selectProps.customProps
    let normalizedResults = []


    if (data.media) {
        normalizedResults = data.media.map(m => APIDataMediaToNormalizedMedia(m))
    }

    return (
        <reactSelectComponents.Option {...props}>
            <ReplyTile
                message={data}
                media={normalizedResults}
                onSelect={onSelect}
                onUpdateNotifyState={(notifyState, message) => updateNotifyState(notifyState, message)}
                loadingNotification={loadingNotification}
            />
        </reactSelectComponents.Option>
    )
})

export { RoomRepliesSearchSelect }
